import { Auction, OfferTransaction } from '@/api';
import i18next from 'i18next';
import React, { useState } from 'react';
import Link from 'next/link';
import Button from '@/components/general/Button';
import { Constants } from '@/helpers/Constants';
import { useFavorite } from '@/hooks/api/useFavorite';
import { usePathname, useRouter } from 'next/navigation';
import useUnAuth from '@/contexts/UnAuthContext';
import { setUpCalendarReminder } from '@/helpers/Calendar';
import MinimumOfferSetModal from '@/components/ecommerce/product/ProductPage/Modals/MinimumOfferSetModal';
import { printShippingLabel } from '@/helpers/ShippingLabel';
import useItemContext from '@/hooks/useItemContext';
import useAuth from '@/contexts/AuthContext';
import useAuction from '@/hooks/useAuction';
import If from '@/components/util/IF';

type Props = {
    isEverythingLoaded: boolean | undefined;
    hasAuctionFlag: boolean | undefined;
    loading: boolean | undefined;
    showShare: (val: boolean) => void;
    showBidding: (val: boolean) => void;

};

const ProductEcomButtons = ({ showShare, showBidding, isEverythingLoaded, hasAuctionFlag }: Props) => {
    const { item: product } = useItemContext();
    const { user } = useAuth();
    const pathname = usePathname();
    const { auction, loading, auctionOver, hasUserAuctionAccess, hasWonAuction, comingSoon } = useAuction(product, undefined, true);
    const { add, remove, isFavorite } = useFavorite();
    const router = useRouter();
    const { showSignUpOrLogin } = useUnAuth();
    const [showSetMinimum, setShowSetMinimum] = useState<boolean>(false);
    const offerTransaction = product?.current_offer_transaction;
    const buyButtonText = () => {
        if (product?.check_offers_possible) {
            if (product.auto_accept_offer) {
                return i18next.t('common:button-text.signup-to-make-offer-instant');
            }
            return i18next.t('common:button-text.signup-to-make-offer');
        }
        if (auction?.type === Auction.TypeEnum.Auction) {
            return i18next.t('common:button-text.signup-to-bid');
        }
        return i18next.t('common:button-text.signup-to-buy');
    };

    const placeBid = isEverythingLoaded && hasAuctionFlag && hasUserAuctionAccess && !auctionOver && auction?.type === Auction.TypeEnum.Auction;
    const remindMe = comingSoon && auction && product;
    const viewInvoice = (isEverythingLoaded && user && !product?.check_offers_possible && (hasWonAuction || (product?.current_offer_transaction?.offer.user_id === user?.id)));
    const showPrintShippingLabel = offerTransaction && user && offerTransaction.status === OfferTransaction.StatusEnum.Paid && product?.owner_id === user?.id;
    const resaleGuide = offerTransaction && offerTransaction.status !== OfferTransaction.StatusEnum.Done;
    const setMinimumOffer = user && product?.owner_id === user?.id && !product?.current_offer_transaction;

    return (
        <>
            <div>
                <If condition={isEverythingLoaded && !user && (!auctionOver || product?.check_offers_possible)}>
                    <div className="md:hidden">
                        <Button
                            fullWidth
                            className="mt-3"
                            href={{ pathname: '/mobile/signup', query: { redirect: pathname } }}
                        >{buyButtonText()}
                        </Button>
                    </div>
                    <div className="hidden md:block">
                        <Button
                            fullWidth
                            className="mt-3"
                            onClick={() => {
                                showSignUpOrLogin('signup');
                            }}
                        >{buyButtonText()}
                        </Button>
                    </div>
                </If>
            </div>

            <div>

                <If condition={placeBid}>
                    <Button
                        fullWidth
                        className="whitespace-nowrap"
                        onClick={async () => showBidding(true)}>
                        {i18next.t('button-text.place-bid')}
                    </Button>
                </If>

                <If condition={remindMe}>
                    <Button
                        fullWidth
                        className="mt-3"
                        onClick={() => setUpCalendarReminder(auction!, product!, user)}>
                        {i18next.t('common:button-text.remind-me')}
                    </Button>
                </If>

                <If condition={viewInvoice}>
                    <Button
                        fullWidth
                        className="mt-3"
                        href={Constants.Links.LINK_SETTINGS_INVOICES}
                        loading={loading}
                        disabled={loading}
                    >{i18next.t('button-text.view-invoice')}
                    </Button>
                </If>

                <If condition={[showPrintShippingLabel]}>
                    <Button
                        fullWidth
                        className="mt-4"
                        onClick={() => printShippingLabel(offerTransaction!)}>{i18next.t('common:offers.print-shipping-label')}
                    </Button>
                </If>

                <If condition={resaleGuide}>
                    <Button
                        fullWidth
                        light
                        className="mt-4"
                        linkTarget="_blank"
                        href={Constants.Support.RESALE_GUIDE}>{i18next.t('common:offers.view-resale-guide')}
                    </Button>
                </If>
                <div className="flex md:flex-row flex-col md:space-x-2">
                    <Button
                        light
                        fullWidth
                        className="mt-4 bg-grey12 hidden md:block"
                        loading={!isEverythingLoaded}
                        onClick={() => {
                            if (!user) {
                                showSignUpOrLogin('signup');
                                return;
                            }
                            if (product && isFavorite(product.id)) {
                                remove(product.id);
                            } else if (product) {
                                add(product.id);
                            }
                        }}>{product && isFavorite(product.id) ? i18next.t('button-text.remove-from-favorites') : i18next.t('button-text.add-to-favorites')}
                    </Button>
                    <div className="md:hidden">
                        <Button
                            fullWidth
                            light
                            className="mt-4"
                            loading={!isEverythingLoaded}
                            onClick={() => {
                                if (!user) {
                                    router.push(`/mobile/signup?redirect=${pathname}`);
                                    return;
                                }
                                if (product && isFavorite(product.id)) {
                                    remove(product.id);
                                } else if (product) {
                                    add(product.id);
                                }
                            }}>{product && product && isFavorite(product.id) ? i18next.t('button-text.remove-from-favorites') : i18next.t('button-text.add-to-favorites')}
                        </Button>
                    </div>

                    <Button
                        fullWidth
                        disabled={!isEverythingLoaded}
                        loading={!isEverythingLoaded}
                        light
                        className="mt-4"
                        onClick={() => showShare(true)}>{i18next.t('general.share')}
                    </Button>
                </div>
                <If condition={setMinimumOffer}>
                    <Button onClick={() => setShowSetMinimum(true)} fullWidth className="mt-[16px]">{i18next.t('common:offers.set-minimum-offer')}</Button>
                </If>
            </div>

            <If condition={hasAuctionFlag && isEverythingLoaded}>
                <div id="cos" className="mt-6 md:mt-4 px-6 md:px-4 scroll-mt-[100px]">
                    <div className="font-[500] text-sm text-blue12 mb-[6px]">{i18next.t('common:disclaimer.bidding.title')}</div>
                    <div className="text-sm font-[400] leading-[150%] text-grey15">{product?.conditions_of_sale || i18next.t('common:disclaimer.bidding.text')}{' '}<Link prefetch={false} className="underline" href="/conditions-of-sale">{i18next.t('common:disclaimer.bidding.read-more')}</Link></div>
                </div>
            </If>

            <If condition={hasAuctionFlag && isEverythingLoaded && (auction?.pickup_options?.length || 0) > 0}>
                <div id="pickup_options" className="mt-6 md:mt-4 px-6 md:px-4 scroll-mt-[100px]">
                    <div className="font-[500] text-sm text-blue12 mb-[6px]">{i18next.t('common:item-text.pickup-options')}</div>
                    <div className="text-sm font-[400] leading-[150%] text-grey15">{auction?.pickup_options?.map((option) => <div key={option}>{option}</div>)}</div>
                </div>
            </If>

            <MinimumOfferSetModal onHide={() => { setShowSetMinimum(false); }} show={showSetMinimum} product={product} />
        </>
    );
};

export default ProductEcomButtons;
